// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { checkoutPages } from "@bg/XDL/constants";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CookiePrivacyPolicyPopup from "@insite/content-library/Components/CookiePrivacyPolicyPopup";
import Icon from "@insite/mobius/Icon";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export interface FooterStyles {
    page?: MobiusPageProps;
    topContent?: InjectableCss;
    topZonesWrapper?: InjectableCss;
    subscribe?: InjectableCss;
    socialIcons?: InjectableCss;
    middleContent?: InjectableCss;
    bottomContent?: InjectableCss;
}

export const footerStyles: FooterStyles = {
    topContent: {
        css: css`
            border-top: 6px solid ${getColor("common.ui1")};
            background: ${getColor("common.ui4")};
            padding-bottom: 40px;
        `,
    },
    topZonesWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: flex-end;

            @media (max-width: 768px) {
                flex-wrap: wrap;
            }
        `,
    },
    subscribe: {
        css: css`
            flex-grow: 1;

            && > div {
                display: flex;
                justify-content: center;
            }

            @media (max-width: 768px) {
                width: 100%;
            }
        `,
    },
    middleContent: {
        css: css`
            background: ${getColor("text.main")};
            a span {
                color: ${getColor("common.white")} !important;
            }
        `,
    },
    bottomContent: {
        css: css`
            background: ${getColor("common.black")};
            & .bottom-links {
                display: flex;
                justify-content: flex-end;
                flex-wrap: wrap
                color: white;
                font-size: 13px;
                line-height: 15px;
                a {
                    margin-right: 12px;
                }
                a span {
                    color: ${getColor("common.white")};
                    font-size: 13px;
                    line-height: 15px;
                }
            
                @media (max-width: 768px) {
                    justify-content: center;
                    flex-wrap: wrap;
                    a{
                        margin-top: 8px;
                    }
                }
            }
        `,
    },
};
const styles = footerStyles;

type Props = PageProps;

const Footer = ({ id }: Props) => {
    const { isCheckoutPage } = useAppSelector(state => ({
        isCheckoutPage: checkoutPages.includes(getCurrentPage(state).type),
    }));

    const copyrightMessage = () => {
        const message = (siteMessage("Custom_Footer_Copyright") as string) || "© {0} SONANCE - All Rights Reserved.";
        return message.replace("{0}", String(new Date().getFullYear()));
    };

    return (
        <Page
            as="footer"
            isFooter={true}
            css={css`
                margin-top: 70px;
                @media (max-width: 1024px) {
                    margin-top: 40px;
                    overflow: hidden;
                }
            `}
            fullWidth={[true, true, true, true, true]}
        >
            {!isCheckoutPage && (
                <>
                    <StyledWrapper>
                        <Zone contentId={id} zoneName="Instagram" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.middleContent}>
                        <Page>
                            <Zone contentId={id} zoneName="ContentMiddle" />
                        </Page>
                    </StyledWrapper>
                </>
            )}
            <StyledWrapper {...styles.bottomContent}>
                <Page
                    css={css`
                        position: relative;
                        padding-top: 20px;
                        padding-bottom: 15px;
                        overflow: hidden;
                    `}
                >
                    <StyledWrapper
                        css={css`
                            display: flex;
                            flex-wrap: wrap;
                            align-items: center;
                            ${!isCheckoutPage &&
                            css`
                                padding-right: 35px;
                            `}
                            @media (max-width: 768px) {
                                flex-direction: column-reverse;
                                padding-right: 0;
                                justify-content: center;
                            }
                        `}
                    >
                        <StyledWrapper
                            css={css`
                                font-size: 13px;
                                line-height: 15px;
                                color: ${getColor("common.ui3")};
                                z-index: 2;
                            `}
                        >
                            {copyrightMessage()}
                        </StyledWrapper>
                        <StyledWrapper
                            css={css`
                                flex-grow: 1;
                                z-index: 2;
                                @media (max-width: 768px) {
                                    margin-bottom: 15px;
                                }
                                .bottom-links {
                                    a span {
                                        font-weight: 400 !important;
                                        color: ${getColor("common.ui3")} !important;
                                    }
                                    a:hover {
                                        color: ${getColor("common.ui3")} !important;
                                    }
                                }
                            `}
                        >
                            <Zone contentId={id} zoneName="ContentBottomLeft" />
                        </StyledWrapper>
                    </StyledWrapper>
                </Page>
            </StyledWrapper>
            <CookiePrivacyPolicyPopup />
            {createWidgetElement("CustomerCreditCheck/CustomerCreditCheck", {
                fields: {},
            })}
        </Page>
    );
};

const pageModule: PageModule = {
    component: Footer,
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;
/**

 * @deprecated Use string literal "Footer" instead of this constant.

 */
export const FooterContext = "Footer";
